import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import styles from './RouteStatusPages.module.css';

/**
 * lazy ルートのチャンク読み込み失敗 → 自動リロードまでの間に出す表示 (utils/lazyRoute.ts 参照)。
 * リロードは即時に呼ばれているので、ここが見えるのは一瞬。
 */
export function RouteReloadingPage() {
  return (
    <div className={styles.page} role="status" aria-live="polite">
      <div className="spinner" />
      <div className={styles.title}>アプリを最新版に更新しています</div>
      <div className={styles.note}>ページを再読み込みしています。そのままお待ちください</div>
    </div>
  );
}

function describeError(error: unknown): string {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`.trim();
  if (error instanceof Error) return error.message;
  return String(error);
}

/**
 * ルート全体の errorElement。描画中の例外や lazy ルートの読み込み失敗 (リロード後も
 * 失敗した場合) を、React Router 既定の "Unexpected Application Error!" の代わりに
 * 日本語の案内 + 再読み込みボタンで表示する。Sentry にも送る (既定のエラー境界は送らない)。
 */
export function RouteErrorPage() {
  const error = useRouteError();

  useEffect(() => {
    console.error('[RouteErrorPage]', error);
    Sentry.captureException(error);
  }, [error]);

  return (
    <div className={styles.page} role="alert">
      <div className={styles.title}>ページの表示中にエラーが発生しました</div>
      <div className={styles.note}>
        再読み込みで直ることがあります。繰り返す場合は、しばらく時間をおいてからお試しください
      </div>
      <div className={styles.note}>{describeError(error)}</div>
      <div className={styles.actions}>
        <a className="btn-cancel" href="/">ホームへ戻る</a>
        <button type="button" className="btn-modal" onClick={() => window.location.reload()}>
          再読み込み
        </button>
      </div>
    </div>
  );
}
